/**
 * hedge.js — Hedge Calculator for the Bet Tracker (Winning Features
 * addendum). Pure, no I/O.
 *
 * The equal-profit hedge: a parlay is one leg from cashing; betting
 * the other side of the last leg at the current price can lock the
 * same profit whichever way the game goes. If the original bet
 * returns stake x combinedDecimal when it wins, then a hedge stake of
 *
 *   hedgeStake = (stake x combinedDecimal) / hedgeDecimal
 *
 * returns exactly the same gross amount when the hedge wins instead,
 * so the profit is identical either way:
 *
 *   profit = stake x combinedDecimal - stake - hedgeStake
 *
 * (positive = locked profit; negative = a locked loss, which the UI
 * labels as such — the math is the same). The price lookup below
 * works on raw Odds API events, the shape oddsApi.fetchOdds returns.
 */

const round2 = (n) => Math.round(n * 100) / 100;

const normalizeName = (name) => String(name || '').trim().toLowerCase();

/**
 * computeHedge({ stake, combinedDecimal, hedgeDecimal })
 * Returns { hedgeStake, guaranteedProfit, grossIfOriginalWins,
 * grossIfHedgeWins } (money rounded to cents), or null when the
 * inputs can't support a hedge (non-positive stake, decimals <= 1).
 */
export function computeHedge({ stake, combinedDecimal, hedgeDecimal } = {}) {
  const s = Number(stake);
  const cd = Number(combinedDecimal);
  const hd = Number(hedgeDecimal);
  if (!Number.isFinite(s) || !Number.isFinite(cd) || !Number.isFinite(hd)) return null;
  if (s <= 0 || cd <= 1 || hd <= 1) return null;
  const hedgeStake = round2((s * cd) / hd);
  return {
    hedgeStake,
    guaranteedProfit: round2(s * cd - s - hedgeStake),
    grossIfOriginalWins: round2(s * cd),
    grossIfHedgeWins: round2(hedgeStake * hd)
  };
}

/** Legs of a stored record that have not settled individually. */
export function remainingLegs(record) {
  return ((record && record.legs) || []).filter((leg) => !leg.result);
}

/**
 * The leg a hedge targets: the last remaining leg to be decided
 * (latest commenceTime among unsettled legs). Null when nothing
 * remains.
 */
export function hedgeTargetLeg(record) {
  const remaining = remainingLegs(record);
  if (remaining.length === 0) return null;
  return remaining
    .slice()
    .sort((a, b) => String(b.commenceTime || '').localeCompare(String(a.commenceTime || '')))[0];
}

function americanToDecimal(american) {
  if (typeof american !== 'number' || american === 0) return null;
  return american > 0 ? 1 + american / 100 : 1 + 100 / Math.abs(american);
}

function pointsMatch(leg, outcome) {
  if (leg.market === 'h2h') return true;
  if (typeof leg.point !== 'number' || typeof outcome.point !== 'number') return false;
  if (leg.market === 'totals') return outcome.point === leg.point;
  // Spreads: the other side carries the mirrored point.
  if (leg.market === 'spreads') return outcome.point === -leg.point;
  return false;
}

/**
 * Best available price for the OPPOSITE side of a tracker leg, from
 * raw Odds API events (fetchOdds shape). The event is matched by the
 * leg's eventId when present, else by the two team names. "Best" is
 * the highest decimal price across every book carrying that exact
 * market/outcome/point. Returns
 * { american, decimal, bookmaker, bookmakerTitle } or null.
 */
export function findHedgePrice(events, leg) {
  if (!leg || !Array.isArray(events)) return null;
  const event = events.find((ev) => leg.eventId && ev.id === leg.eventId) ||
    events.find((ev) =>
      normalizeName(ev.home_team) === normalizeName(leg.homeTeam) &&
      normalizeName(ev.away_team) === normalizeName(leg.awayTeam));
  if (!event || !Array.isArray(event.bookmakers)) return null;

  let best = null;
  for (const bookmaker of event.bookmakers) {
    const market = (bookmaker.markets || []).find((m) => m.key === leg.market);
    if (!market || !Array.isArray(market.outcomes)) continue;
    for (const outcome of market.outcomes) {
      if (normalizeName(outcome.name) === normalizeName(leg.name)) continue;
      if (!pointsMatch(leg, outcome)) continue;
      const decimal = americanToDecimal(outcome.price);
      if (decimal === null) continue;
      if (!best || decimal > best.decimal) {
        best = {
          american: outcome.price,
          decimal,
          bookmaker: bookmaker.key,
          bookmakerTitle: bookmaker.title || bookmaker.key
        };
      }
    }
  }
  return best;
}

export default computeHedge;
